import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import ViewColumnOutlinedIcon from "@mui/icons-material/ViewColumnOutlined";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import Button from "../../../../../design/button";
import CheckboxPopover from "../../../../../design/checkbox-popover";
import { SelectedWorkerSlot } from "../../../types/direct-assignment";
import AssignShiftTable from "../../shift-details/assign-shift-table";
import { WorkersPropsV2 } from "../shift-details-dialog/types";
import useDirectAssignPicker, {
	DirectAssignShift,
} from "./use-direct-assign-picker";

interface AssignWorkersStepProps {
	shift: DirectAssignShift | null;
	workers: WorkersPropsV2[];
	isLoading: boolean;
	initialSelection: SelectedWorkerSlot[];
	isWorkerSubTypeEnabled: boolean;
	shiftTimeLabel: string;
	// The Job Role colored box, or the skills line for a Shift by skills
	jobRoleLabel: React.ReactNode;
	// "Location · Work Center · N Required Workers"
	shiftDetailsLabel: string;
	subtitle: string;
	onCancel: () => void;
	onSave: (selection: SelectedWorkerSlot[]) => void;
}

const AssignWorkersStep = ({
	shift,
	workers,
	isLoading,
	initialSelection,
	isWorkerSubTypeEnabled,
	shiftTimeLabel,
	jobRoleLabel,
	shiftDetailsLabel,
	subtitle,
	onCancel,
	onSave,
}: AssignWorkersStepProps) => {
	const { t } = useTranslation();
	const [columnAnchorElement, setColumnAnchorElement] =
		useState<HTMLElement | null>(null);
	const {
		rows,
		search,
		setSearch,
		workersSelected,
		setWorkersSelected,
		columnOptions,
		setColumnOptions,
		shiftMetadata,
		workersRequired,
		canSelectMore,
		selection,
		isSelectionValid,
	} = useDirectAssignPicker({
		workers,
		shift,
		initialSelection,
		isWorkerSubTypeEnabled,
	});

	const handleSave = useCallback(() => {
		onSave(selection);
	}, [onSave, selection]);

	const handleOpenColumns = useCallback(
		(e: React.MouseEvent<HTMLElement>) => {
			setColumnAnchorElement(e.currentTarget);
		},
		[]
	);

	const handleCloseColumns = useCallback(() => {
		setColumnAnchorElement(null);
	}, []);

	const handleSearchChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			setSearch(e.target.value);
		},
		[setSearch]
	);

	const handleClearSearch = useCallback(() => {
		setSearch("");
	}, [setSearch]);

	return (
		<Box display='flex' flexDirection='column'>
			<Box
				sx={{
					display: "flex",
					alignItems: "flex-start",
					gap: 1,
					padding: "24px 24px 0",
				}}
			>
				<IconButton
					onClick={onCancel}
					aria-label={t("scheduler_back")}
					sx={{ padding: "4px" }}
				>
					<ArrowBackIcon />
				</IconButton>
				<Box display='flex' flexDirection='column'>
					<Typography
						variant='h5'
						sx={{ fontSize: "24px", lineHeight: "31px" }}
					>
						{t("scheduler_assign-workers")}
					</Typography>
					<Box
						sx={{
							display: "flex",
							flexWrap: "wrap",
							alignItems: "center",
							gap: 1.5,
							marginTop: "12px",
						}}
					>
						<Typography sx={{ fontSize: "16px", lineHeight: "28px" }}>
							{shiftTimeLabel}
						</Typography>
						{jobRoleLabel}
						<Typography
							sx={{
								fontSize: "16px",
								lineHeight: "28px",
								color: "rgba(0, 0, 0, 0.6)",
							}}
						>
							{shiftDetailsLabel}
						</Typography>
					</Box>
					<Typography
						sx={{ fontSize: "16px", lineHeight: "28px", marginTop: "4px" }}
					>
						{subtitle}
					</Typography>
				</Box>
			</Box>
			<Box sx={{ padding: "0 24px" }}>
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						height: "57px",
						gap: 2,
					}}
				>
					<IconButton
						onClick={handleOpenColumns}
						aria-label={t("scheduler_columns")}
						sx={{
							padding: "4px",
							borderRadius: "4px",
							background: "rgba(0, 0, 0, 0.04)",
						}}
					>
						<ViewColumnOutlinedIcon />
					</IconButton>
					<CheckboxPopover
						anchorElement={columnAnchorElement}
						options={columnOptions}
						setOptions={setColumnOptions}
						onClose={handleCloseColumns}
						selectAllCheckboxOption={false}
						minWidth={220}
						disableMaxHeight
					/>
					<TextField
						hiddenLabel
						value={search}
						onChange={handleSearchChange}
						placeholder={t("common_search")}
						variant='filled'
						size='small'
						InputProps={{
							disableUnderline: true,
							startAdornment: (
								<SearchIcon
									sx={{ height: 24, width: 24, color: "rgba(0,0,0,0.56)" }}
								/>
							),
							endAdornment:
								search !== "" ? (
									<ClearIcon
										onClick={handleClearSearch}
										sx={{
											height: 24,
											width: 24,
											color: "rgba(0,0,0,0.56)",
											cursor: "pointer",
										}}
									/>
								) : null,
						}}
						sx={{
							flex: 1,
							border: "1px solid rgba(0,0,0,0.12)",
							borderRadius: "4px",
							input: {
								paddingLeft: "8px",
								color: "rgba(0,0,0,0.87)",
								fontSize: "12px",
								lineHeight: "20px",
								letterSpacing: "0.4px",
							},
						}}
					/>
				</Box>
				{isLoading ? (
					<Box
						display='flex'
						alignItems='center'
						justifyContent='center'
						height={320}
					>
						<CircularProgress />
					</Box>
				) : (
					<AssignShiftTable
						rows={rows}
						columns={columnOptions}
						shiftMetadata={shiftMetadata}
						workersSelected={workersSelected}
						setWorkersSelected={setWorkersSelected}
						searchValue={search}
						canSelectRow={canSelectMore}
						dimUnselectableRows
						isFullWidth
						detailPanelExpandedRowIds={[]}
					/>
				)}
			</Box>
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					padding: "24px",
				}}
			>
				<Typography sx={{ fontSize: "14px", color: "rgba(0, 0, 0, 0.6)" }}>
					{t("scheduler_n-of-m-selected", {
						n: workersSelected.length,
						m: workersRequired,
					})}
				</Typography>
				<Box display='flex' alignItems='center'>
					<Button
						color='primary'
						variant='text'
						onClick={onCancel}
						customStyles={{ marginRight: "16px" }}
					>
						{t("common_cancel")}
					</Button>
					<Button
						color='primary'
						variant='contained'
						onClick={handleSave}
						disabled={isLoading || !isSelectionValid}
					>
						{t("common_save")}
					</Button>
				</Box>
			</Box>
		</Box>
	);
};

export default AssignWorkersStep;
